// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

// doc-exempt: a copy of AYNI 3D's src/main/ai/ai-orchestrator.ts; never edit it here (scripts/sync-ayni3d-copy.ts)
/**
 * The AYNI chatbot's conversation engine, in the main process
 * (docs/AI_CHATBOT_PLAN.md §3-4). One engine serves every surface; each
 * session is one conversation. A turn:
 *   1. plain commands and exact "show <title>" requests act without a model;
 *   2. otherwise the model gets the instructions, the recent conversation and
 *      a context block (what the sphere shows, the catalog entries matching
 *      the visitor's words), and may call the agent tools;
 *   3. every tool call is validated before it runs, through the same
 *      AgentCommand path any controller uses; an invalid call is answered
 *      with the reason once, and the turn ends if the model repeats it.
 * Events stream to the surface as they happen: text, one per action, then
 * exactly one done or error.
 */
import type { AiChatErrorCode, AiChatEvent, AiChatMode, AiChatSendRequest, AiChatSurface } from '../../shared/ai-chat-protocol';
import { buildAiToolDefinitions, toolCallToAgentCommand, type AiToolDefinition } from '../../shared/ai-tools';
import { getAgentCapabilities } from '../../shared/agent-capabilities';
import type { AgentCatalogResult, AgentCommand, AgentForwarderResult, AgentKhipuEntry, AgentObservation } from '../../shared/protocol';
import type { Logger } from '../../shared/logger/types';
import { buildSystemPrompt, buildTurnMessage, visitorMessage } from './ai-prompt';
import { DECLINE_REPLY, DISTRESS_REPLY, isDistress, redactPersonalData, screenReply } from '../../shared/ai-safety';
import { transcribeSpeech, type TranscriptionResult } from './speech-to-text';
import { synthesizeSpeech, type SpeechResult } from './text-to-speech';
import { toSpeakableText } from '../../shared/ai-speakable-text';
import type { AiChatLanguage } from '../../shared/ai-languages';
import { formatDatasetNotes, isTimelineSeekable, mediaSecondsForDate, yearsCovered, type DatasetNotes } from './dataset-notes';
import {
  ACTION_FAILED_REPLY,
  DONE_REPLY,
  LOADING_REPLY,
  NO_ANSWER_REPLY,
  SEEK_YEAR_REPLY,
  YEAR_OUT_OF_RANGE_REPLY,
  matchFastPath,
  matchLoadRequest,
  matchYearRequest,
  normalizeCommandText
} from './ai-fast-path';
import { AI_MODERATION_HEADER, ChatEndpointError, streamChatCompletion, type ChatEndpoint, type ChatMessage, type ChatToolCall } from './openai-chat-client';

/** Most model rounds in one turn (each may call tools). */
export const MAX_AI_TOOL_ROUNDS = 4;
/** Recent messages kept verbatim; older ones are dropped. */
export const MAX_AI_HISTORY_MESSAGES = 12;
/** A tool result is cut to this many characters before the model sees it. */
export const MAX_AI_TOOL_RESULT_CHARS = 3000;
/**
 * Longer results for the commands whose text is the answer: a methods
 * paragraph with its references, a citation in BibTeX or RIS.
 */
export const AI_TOOL_RESULT_CHAR_LIMITS: Readonly<Partial<Record<AgentCommand['type'], number>>> = {
  'methods.get': 8000,
  'cite.view': 4000
};
/**
 * Limits on what one turn may do to the picture (docs/AI_CHATBOT_SAFETY.md
 * §4): a public sphere must never flash or flicker through datasets because a
 * model, or someone steering it, asked for many changes at once. Calls over a
 * limit are answered "not run" and the model explains.
 */
/** Most tool calls one model round may make. */
export const MAX_AI_TOOL_CALLS_PER_ROUND = 4;
/** Most commands in one turn that change what is on screen. */
export const MAX_AI_SCREEN_CHANGES_PER_TURN = 6;
/** Most datasets one turn may load. */
export const MAX_AI_DATASET_LOADS_PER_TURN = 2;
/**
 * The shortest time between the starts of two changes to the picture: one a
 * second, well under the three flashes a second that WCAG 2.3.1 sets.
 */
export const AI_MIN_SCREEN_CHANGE_GAP_MS = 1000;
/**
 * Commands that leave the picture as it is. Every other command counts as a
 * change, so a command added later is limited until it is listed here.
 */
const READS_ONLY: ReadonlySet<AgentCommand['type']> = new Set<AgentCommand['type']>([
  'catalog.search', 'state.describe', 'luma.describe', 'luma.probe', 'luma.probeMany', 'luma.stats', 'luma.extremum',
  'luma.transect', 'luma.zonal', 'pins.read', 'stats.trend', 'stats.emergence', 'stats.correlation', 'baseline.status',
  'threshold.area', 'hovmoller.summary', 'methods.get', 'cite.view', 'lesson.find', 'lesson.context', 'lesson.hint',
  'misconception.check', 'explain.value', 'features.query', 'satellites.sources', 'satellites.datasets', 'satellites.passes',
  'khipu.list'
]);
/** A turn still running after this long is stopped, with a reply saying so. */
export const AI_TURN_TIMEOUT_MS = 60_000;
/** A conversation idle this long starts over. */
export const AI_SESSION_IDLE_MS = 30 * 60 * 1000;
const MAX_SESSIONS = 200;

export type AiEndpointResolution = { ok: true; endpoint: ChatEndpoint } | { ok: false; code: AiChatErrorCode; message: string };

export interface AiOrchestratorHost {
  logger: Logger;
  /** Whether the chatbot is switched on at all. */
  isEnabled(): Promise<boolean>;
  /** The model endpoint, or why there is none. */
  resolveEndpoint(): Promise<AiEndpointResolution>;
  /** Where speech goes (a local speech server); absent: the chat endpoint. */
  resolveSpeechEndpoint?(): Promise<AiEndpointResolution>;
  /** Runs a control command through the AgentCommand path (validated again there). */
  executeAgentCommand(command: AgentCommand): Promise<AgentForwarderResult>;
  searchCatalog(query: string, limit: number): Promise<AgentCatalogResult[]>;
  /**
   * Commands this app carries out beyond the shared defaults, offered on
   * surfaces with no list of their own (AYNI 3D: the Khipu commands).
   */
  extraCommands?: ReadonlyArray<AgentCommand['type']>;
  /** The Khipus in this app's library matching the words (khipu.list). */
  listKhipus?(query: string | undefined, limit: number): Promise<AgentKhipuEntry[]>;
  /** The publisher's notes for a dataset, when the app has them. */
  datasetNotes?(datasetId: string): Promise<DatasetNotes | null>;
  /** For tests. */
  now?(): number;
  /** Waits before the next change to the picture; tests pass one that does not. */
  wait?(ms: number, signal: AbortSignal): Promise<void>;
  streamChat?: typeof streamChatCompletion;
  transcribe?: typeof transcribeSpeech;
  synthesize?: typeof synthesizeSpeech;
}

interface Session {
  /** The mode the history was built in; null for a host without modes. */
  mode: AiChatMode | null;
  history: ChatMessage[];
  lastActivity: number;
  abort: AbortController | null;
  /** When the session last started a change to the picture (epoch ms). */
  lastScreenChange: number;
}

/** What one turn has done to the picture so far, against the limits above. */
interface TurnBudget {
  screenChanges: number;
  loads: number;
}

function defaultWait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new ChatEndpointError('AI_CANCELLED', 'Stopped.'));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new ChatEndpointError('AI_CANCELLED', 'Stopped.'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export interface AiTurnContext {
  surface: AiChatSurface;
  /** Narrows the tools, e.g. a kiosk's slice-scoped set. Absent: every enabled capability. */
  allowedCommands?: ReadonlySet<AgentCommand['type']>;
  /**
   * Runs this surface's commands, e.g. a kiosk's on its own slice only.
   * Absent: the host's executeAgentCommand (the whole sphere).
   */
  execute?: (command: AgentCommand) => Promise<AgentForwarderResult>;
  /**
   * More facts about this surface for the turn's context block, for example
   * a game in progress on a kiosk. Information for the model, not instructions.
   */
  situation?: string;
  /**
   * Who the chatbot is helping (AYNI XR's Research and Education
   * environments). Absent: today's behaviour. A turn in another mode than
   * the session's starts a fresh history.
   */
  mode?: AiChatMode;
  /** Student mode: guide with questions (default true). */
  socratic?: boolean;
}

/**
 * Whether anyone at all may be on the other side: a kiosk, the website, or a
 * student. The gateway then reads the questions and answers with its guard
 * model (docs/AI_CHATBOT_SAFETY.md §5).
 */
export function isPublicTurn(context: Pick<AiTurnContext, 'surface' | 'mode'>): boolean {
  return context.surface === 'kiosk' || context.surface === 'web' || context.mode === 'student';
}

function sessionKey(surface: AiChatSurface, sessionId: string): string {
  return `${surface}:${sessionId}`;
}

function describeError(error: unknown): { code: AiChatErrorCode; message: string } {
  if (error instanceof ChatEndpointError) return { code: error.code, message: error.message };
  return { code: 'AI_FAILED', message: error instanceof Error ? error.message : String(error) };
}

function clipResult(value: unknown, limit = MAX_AI_TOOL_RESULT_CHARS): string {
  let text: string;
  try {
    text = JSON.stringify(value ?? null);
  } catch {
    text = String(value);
  }
  return text.length > limit ? `${text.slice(0, limit)}… (cut)` : text;
}

/**
 * A command's result as the model may see it in this mode: teacher-only
 * material (an AgentToolOutcome's `teacher`) only in teacher mode. Defence
 * in depth; the host keeps such material from a student's page as well.
 */
export function resultForMode(result: unknown, mode: AiChatMode | undefined): unknown {
  if (mode === 'teacher' || typeof result !== 'object' || result === null || !('teacher' in result)) return result;
  const { teacher: _teacher, ...rest } = result as Record<string, unknown>;
  return rest;
}

/**
 * What an action did, for the surface's action chip, with no words in any
 * language: the surface puts its own label ("Loaded", "Cargado"…) in front.
 * Empty when the label says it all ("Paused").
 */
function summarizeAction(
  command: AgentCommand,
  result: AgentForwarderResult | { success: true; result: unknown },
  titles?: ReadonlyMap<string, string>
): string {
  if (!result.success) return 'error' in result && result.error ? result.error.message : '';
  const observation = 'observation' in result ? result.observation : undefined;
  switch (command.type) {
    case 'catalog.search': {
      const count = (result.result as { count?: number } | undefined)?.count ?? 0;
      return `“${command.query}” · ${count}`;
    }
    case 'dataset.load':
      // A kiosk acts on its slice and reports no observation: use the title
      // the turn already knows from the catalog.
      return observation?.currentDataset?.title ?? titles?.get(command.datasetId) ?? command.datasetId;
    case 'view.goto':
      return `${command.latitude.toFixed(2)}, ${command.longitude.toFixed(2)}`;
    case 'view.zoom':
      return `×${command.zoom}`;
    case 'view.rotation':
      return command.enabled ? '✓' : '✕';
    case 'media.seek':
      return `${Math.round(command.time)} s`;
    case 'media.seekDate': {
      const shown = (result.result as { shown?: unknown } | undefined)?.shown;
      return typeof shown === 'string' ? shown.slice(0, 16).replace('T', ' ') : command.date;
    }
    case 'media.speed':
      return `×${command.speed}`;
    default: {
      // Read generically: a command only one app has (AYNI 3D's sphere.slice)
      // cannot be named here, since this file is shared.
      const count = (command as { count?: unknown }).count;
      return typeof count === 'number' ? String(count) : '';
    }
  }
}

export class AiOrchestrator {
  private readonly sessions = new Map<string, Session>();

  constructor(private readonly host: AiOrchestratorHost) {}

  private now(): number {
    return this.host.now?.() ?? Date.now();
  }

  private session(surface: AiChatSurface, sessionId: string, mode: AiChatMode | null): Session {
    const key = sessionKey(surface, sessionId);
    const now = this.now();
    let session = this.sessions.get(key);
    if (session && !session.abort && now - session.lastActivity > AI_SESSION_IDLE_MS) {
      session = undefined;
    }
    if (!session) {
      session = { mode, history: [], lastActivity: now, abort: null, lastScreenChange: 0 };
      this.sessions.set(key, session);
      if (this.sessions.size > MAX_SESSIONS) {
        const oldest = [...this.sessions.entries()]
          .filter(([, entry]) => !entry.abort)
          .sort(([, a], [, b]) => a.lastActivity - b.lastActivity)[0];
        if (oldest) this.sessions.delete(oldest[0]);
      }
    }
    if (session.mode !== mode && !session.abort) {
      // A change of role (a student, then a teacher) never shares a history.
      session.history = [];
      session.mode = mode;
    }
    session.lastActivity = now;
    return session;
  }

  /**
   * Speech to text for a surface's mic, through the same endpoint and key as
   * the chat (the text then comes back as an ordinary turn). Never the audio in the log.
   */
  public async transcribe(audio: Uint8Array, language: AiChatLanguage): Promise<TranscriptionResult> {
    if (!(await this.host.isEnabled())) return { ok: false, code: 'AI_DISABLED', message: 'The assistant is switched off.' };
    const endpoint = await this.speechEndpoint();
    if (!endpoint.ok) return { ok: false, code: endpoint.code, message: endpoint.message };
    const result = await (this.host.transcribe ?? transcribeSpeech)({ endpoint: endpoint.endpoint, audio, language });
    this.host.logger.info('[AI] Transcription', { language, bytes: audio.length, ok: result.ok, code: result.ok ? undefined : result.code });
    return result;
  }

  /**
   * One piece of a reply, spoken: numbers and markdown made speakable in the
   * language first (the voices misread digits), then the speech endpoint.
   */
  public async speak(text: string, language: AiChatLanguage): Promise<SpeechResult> {
    if (!(await this.host.isEnabled())) return { ok: false, code: 'AI_DISABLED', message: 'The assistant is switched off.' };
    const speakable = toSpeakableText(text, language);
    if (!speakable) return { ok: false, code: 'AI_INVALID_REQUEST', message: 'Nothing to say.' };
    const endpoint = await this.speechEndpoint();
    if (!endpoint.ok) return { ok: false, code: endpoint.code, message: endpoint.message };
    const result = await (this.host.synthesize ?? synthesizeSpeech)({ endpoint: endpoint.endpoint, text: speakable, language });
    this.host.logger.info('[AI] Speech', { language, chars: speakable.length, ok: result.ok, code: result.ok ? undefined : result.code });
    return result;
  }

  private speechEndpoint(): Promise<AiEndpointResolution> {
    return this.host.resolveSpeechEndpoint ? this.host.resolveSpeechEndpoint() : this.host.resolveEndpoint();
  }

  /** Stops the session's turn in progress, if any. */
  public cancel(surface: AiChatSurface, sessionId: string): void {
    this.sessions.get(sessionKey(surface, sessionId))?.abort?.abort();
  }

  /** Forgets the conversation (and stops a turn in progress). */
  public reset(surface: AiChatSurface, sessionId: string): void {
    this.cancel(surface, sessionId);
    this.sessions.delete(sessionKey(surface, sessionId));
  }

  public async runTurn(request: AiChatSendRequest, context: AiTurnContext, emit: (event: AiChatEvent) => void): Promise<void> {
    const { sessionId, turnId } = request;
    const fail = (code: AiChatErrorCode, message: string): void => emit({ type: 'error', sessionId, turnId, code, message });

    if (!(await this.host.isEnabled())) {
      fail('AI_DISABLED', 'The assistant is switched off.');
      return;
    }
    const session = this.session(context.surface, sessionId, context.mode ?? null);
    if (session.abort) {
      fail('AI_BUSY', 'The assistant is still answering the previous message.');
      return;
    }
    const abort = new AbortController();
    session.abort = abort;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      abort.abort();
    }, AI_TURN_TIMEOUT_MS);
    try {
      if (isDistress(request.text)) {
        // Someone in distress gets people who can help, at once, from no model.
        this.say(request, DISTRESS_REPLY[request.language], emit);
        this.host.logger.info('[AI] Turn answered for safety', { surface: context.surface, reason: 'distress' });
        emit({ type: 'done', sessionId, turnId, source: 'safety' });
        return;
      }
      const handled = await this.tryWithoutModel(request, context, session, emit);
      if (handled) return;
      await this.runModelTurn({ request, context, session, signal: abort.signal, emit });
    } catch (error) {
      if (error instanceof ChatEndpointError && error.policy) {
        // The gateway's moderation declined the question or the answer: a
        // kind reply in the visitor's language, and nothing kept in memory.
        this.say(request, error.policy === 'self-harm' ? DISTRESS_REPLY[request.language] : DECLINE_REPLY[request.language], emit);
        this.host.logger.info('[AI] Turn answered for safety', { surface: context.surface, reason: error.policy });
        emit({ type: 'done', sessionId, turnId, source: 'safety' });
        return;
      }
      const { code, message } = timedOut
        ? { code: 'AI_TIMEOUT' as const, message: 'The turn took too long.' }
        : abort.signal.aborted ? { code: 'AI_CANCELLED' as const, message: 'Stopped.' } : describeError(error);
      if (code !== 'AI_CANCELLED') this.host.logger.warn('[AI] Turn failed', { surface: context.surface, code, message });
      fail(code, message);
    } finally {
      clearTimeout(timer);
      session.abort = null;
      session.lastActivity = this.now();
    }
  }

  private allowed(context: AiTurnContext, type: AgentCommand['type']): boolean {
    return !context.allowedCommands || context.allowedCommands.has(type);
  }

  /** A fixed reply, as one piece of text; it is not kept in the conversation's memory. */
  private say(request: AiChatSendRequest, text: string, emit: (event: AiChatEvent) => void): void {
    emit({ type: 'delta', sessionId: request.sessionId, turnId: request.turnId, text });
  }

  /** Plain commands and exact "show <title>" requests: act, confirm, no model. */
  private async tryWithoutModel(
    request: AiChatSendRequest,
    context: AiTurnContext,
    session: Session,
    emit: (event: AiChatEvent) => void
  ): Promise<boolean> {
    const { sessionId, turnId, language } = request;
    let command: AgentCommand | null = null;
    let reply: ((ok: boolean, detail: string) => string) | null = null;
    const titles = new Map<string, string>();

    // "Take me to 1960": exact when the publisher states the playback rate.
    const year = matchYearRequest(request.text);
    const yearPlan = year === null ? null : await this.planYearSeek(year, context);
    if (yearPlan?.kind === 'outside') {
      const text = YEAR_OUT_OF_RANGE_REPLY[language](yearPlan.years);
      emit({ type: 'delta', sessionId, turnId, text });
      this.remember(session, request.text, text);
      emit({ type: 'done', sessionId, turnId, source: 'fast-path' });
      return true;
    }

    const fast = yearPlan ? null : matchFastPath(request.text);
    if (yearPlan && year !== null) {
      command = yearPlan.kind === 'seek' ? { type: 'media.seek', time: yearPlan.time } : { type: 'media.seekDate', date: yearPlan.date };
      reply = (ok, detail) => (ok ? SEEK_YEAR_REPLY[language](year) : ACTION_FAILED_REPLY[language](detail));
    } else if (fast && this.allowed(context, fast.command.type)) {
      command = fast.command;
      reply = (ok, detail) => (ok ? fast.reply[language] : ACTION_FAILED_REPLY[language](detail));
    } else {
      const title = matchLoadRequest(request.text);
      if (title && this.allowed(context, 'dataset.load')) {
        // The exact title may not rank first ("Earthquakes" vs "120 Years of Earthquakes…").
        const best = (await this.host.searchCatalog(title, 10)).find((entry) => normalizeCommandText(entry.title) === title);
        if (best) {
          command = { type: 'dataset.load', datasetId: best.id };
          titles.set(best.id, best.title);
          reply = (ok, detail) => (ok ? LOADING_REPLY[language](best.title) : ACTION_FAILED_REPLY[language](detail));
        }
      }
    }
    if (!command || !reply) return false;

    const result = await this.run(context, command);
    if (result.success && command.type === 'media.seek' && yearPlan && this.allowed(context, 'media.pause')) {
      // A year the visitor asked for stays on screen (media.seekDate pauses itself).
      await this.run(context, { type: 'media.pause' });
    }
    const summary = summarizeAction(command, result, titles);
    emit({ type: 'action', sessionId, turnId, tool: command.type, ok: result.success, summary });
    const text = reply(result.success, result.error?.message ?? summary);
    emit({ type: 'delta', sessionId, turnId, text });
    this.remember(session, request.text, text);
    emit({ type: 'done', sessionId, turnId, source: 'fast-path' });
    return true;
  }

  /** The notes for the dataset on screen, if the app has them; never an error. */
  private async notesFor(observation: AgentObservation | null): Promise<DatasetNotes | null> {
    const id = observation?.currentDataset?.id;
    if (!id || !this.host.datasetNotes) return null;
    try {
      return await this.host.datasetNotes(id);
    } catch {
      return null;
    }
  }

  /**
   * Where a year plays in the dataset on screen: a media time, "outside" its
   * period, or null (no stated rate, so the model answers with the notes).
   */
  private async planYearSeek(
    year: number,
    context: AiTurnContext
  ): Promise<{ kind: 'seek'; time: number } | { kind: 'seekDate'; date: string } | { kind: 'outside'; years: string } | null> {
    if (!this.allowed(context, 'media.seek') && !this.allowed(context, 'media.seekDate')) return null;
    const observation = await this.observe(context);
    const timeline = (await this.notesFor(observation))?.timeline;
    if (!isTimelineSeekable(timeline) || !this.allowed(context, 'media.seek')) {
      return this.planYearOnScreenTimeline(year, observation, context);
    }
    const startYear = Number(timeline.start.slice(0, 4));
    const endYear = Number(timeline.end.slice(0, 4));
    if (year < startYear || year > endYear) return { kind: 'outside', years: yearsCovered(timeline) };
    // Aim at mid-year, inside the period: well within that year's frames, so
    // a fraction of a second either way still shows the right year.
    const midYear = `${year}-07-01`;
    const date = midYear < timeline.start ? timeline.start : midYear > timeline.end ? timeline.end : midYear;
    const time = mediaSecondsForDate(timeline, date);
    const duration = (observation?.media as { duration?: unknown } | undefined)?.duration;
    if (time === null || (typeof duration === 'number' && duration > 0 && time > duration)) {
      return { kind: 'outside', years: yearsCovered(timeline) };
    }
    return { kind: 'seek', time: Math.round(time * 10) / 10 };
  }

  /**
   * A year on the timeline the dataset itself declares (the time label's
   * range): the renderer seeks by the label's own mapping (media.seekDate).
   */
  private planYearOnScreenTimeline(
    year: number,
    observation: AgentObservation | null,
    context: AiTurnContext
  ): { kind: 'seekDate'; date: string } | { kind: 'outside'; years: string } | null {
    const onScreen = observation?.timeline;
    if (!onScreen || !this.allowed(context, 'media.seekDate')) return null;
    const startYear = Number(onScreen.start.slice(0, 4));
    const endYear = Number(onScreen.end.slice(0, 4));
    if (year < startYear || year > endYear) {
      return { kind: 'outside', years: startYear === endYear ? String(startYear) : `${startYear}–${endYear}` };
    }
    const midYear = new Date(Date.UTC(year, 6, 1)).toISOString();
    const date = midYear < onScreen.start ? onScreen.start : midYear > onScreen.end ? onScreen.end : midYear;
    return { kind: 'seekDate', date };
  }

  /** Keeps a finished exchange: the visitor's words fenced and without personal details. */
  private remember(session: Session, userText: string, assistantText: string): void {
    session.history.push({ role: 'user', content: visitorMessage(redactPersonalData(userText)) }, { role: 'assistant', content: assistantText });
    if (session.history.length > MAX_AI_HISTORY_MESSAGES) {
      session.history.splice(0, session.history.length - MAX_AI_HISTORY_MESSAGES);
    }
  }

  private async runModelTurn(turn: {
    request: AiChatSendRequest;
    context: AiTurnContext;
    session: Session;
    signal: AbortSignal;
    emit: (event: AiChatEvent) => void;
  }): Promise<void> {
    const { request, context, session, signal, emit } = turn;
    const { sessionId, turnId, language } = request;
    const endpoint = await this.host.resolveEndpoint();
    if (!endpoint.ok) {
      emit({ type: 'error', sessionId, turnId, code: endpoint.code, message: endpoint.message });
      return;
    }

    // Personal details never leave the device, not even to the model.
    const text = redactPersonalData(request.text);
    const [observation, matches] = await Promise.all([this.observe(context), this.host.searchCatalog(text, 5).catch(() => [])]);
    const notes = await this.notesFor(observation);
    const mediaSeconds = (observation?.media as { currentTime?: unknown } | undefined)?.currentTime;
    const offered = this.offeredCommands(context);
    const tools = buildAiToolDefinitions(getAgentCapabilities(), offered);
    const headers = isPublicTurn(context) ? { [AI_MODERATION_HEADER]: 'public' } : undefined;
    const messages: ChatMessage[] = [
      {
        role: 'system',
        content: buildSystemPrompt(language, context.surface, context.mode ? { mode: context.mode, socratic: context.socratic } : undefined)
      },
      ...session.history,
      {
        role: 'user',
        content: buildTurnMessage({
          text,
          language,
          observation,
          matches,
          notes: notes ? formatDatasetNotes(notes, typeof mediaSeconds === 'number' ? mediaSeconds : undefined) : null,
          now: this.now(),
          situation: context.situation ?? null
        })
      }
    ];
    // Titles the turn has seen, for action chips when a surface reports no observation.
    const titles = new Map(matches.map((match) => [match.id, match.title]));

    let answer = '';
    let withheld = false;
    let invalidCalls = 0;
    let rounds = 0;
    // Each round's words are checked whole before anyone reads or hears them;
    // a reply that fails stops the turn, and only a plain decline is shown.
    const show = (text: string): void => {
      if (withheld || !text) return;
      const verdict = screenReply(answer + text);
      if (!verdict.ok) {
        withheld = true;
        answer = DECLINE_REPLY[language];
        emit({ type: 'delta', sessionId, turnId, text: answer });
        this.host.logger.warn('[AI] Reply withheld', { surface: context.surface, reason: verdict.reason });
        return;
      }
      answer += text;
      emit({ type: 'delta', sessionId, turnId, text });
    };
    const budget: TurnBudget = { screenChanges: 0, loads: 0 };
    const acted: string[] = [];
    for (; rounds < MAX_AI_TOOL_ROUNDS; rounds++) {
      const round = await this.streamRound({ endpoint: endpoint.endpoint, messages, tools, signal, headers });
      show(round.text);
      if (withheld || !round.calls.length) break;

      messages.push({ role: 'assistant', content: null, tool_calls: round.calls });
      let anyInvalid = false;
      for (const [index, call] of round.calls.entries()) {
        // Every call gets an answer, as the protocol requires; past the limit it is "not run".
        const outcome = index < MAX_AI_TOOL_CALLS_PER_ROUND
          ? await this.runToolCall(call, { context, offered, request, session, budget, signal, emit, titles })
          : { valid: true, acted: false, content: clipResult({ error: `Not run: at most ${MAX_AI_TOOL_CALLS_PER_ROUND} tool calls at once.` }) };
        if (!outcome.valid) anyInvalid = true;
        if (outcome.acted) acted.push(call.function.name);
        messages.push({ role: 'tool', tool_call_id: call.id, content: outcome.content });
      }
      if (anyInvalid && ++invalidCalls > 1) {
        // One correction is allowed; a second invalid call ends the turn
        // rather than letting a confused model keep trying.
        break;
      }
      if (signal.aborted) throw new ChatEndpointError('AI_CANCELLED', 'Stopped.');
    }

    // Every turn ends in words. A model that only called tools, or only
    // thought, gets one more round with no tools; if that is empty too, a
    // plain reply in the visitor's language stands in.
    if (!withheld && !answer.trim()) {
      show((await this.streamRound({ endpoint: endpoint.endpoint, messages, tools: [], signal, headers })).text);
      rounds++;
    }
    if (!answer.trim()) {
      show(acted.length ? DONE_REPLY[language] : NO_ANSWER_REPLY[language]);
    }

    // A withheld reply is forgotten with the question that drew it, so a
    // trick does not carry into the next turn.
    if (!withheld) this.remember(session, request.text, answer.trim());
    this.host.logger.info('[AI] Turn done', {
      surface: context.surface, language, rounds, tools: acted, chars: answer.length, withheld
    });
    emit({ type: 'done', sessionId, turnId, source: withheld ? 'safety' : 'model' });
  }

  /**
   * The commands this turn offers: the surface's own list, or (with none)
   * every enabled capability plus the app's extras. Undefined: the enabled ones.
   */
  private offeredCommands(context: AiTurnContext): ReadonlySet<AgentCommand['type']> | undefined {
    if (context.allowedCommands || !this.host.extraCommands?.length) return context.allowedCommands;
    const enabled = getAgentCapabilities().filter((capability) => capability.enabled).map((capability) => capability.name);
    return new Set([...enabled, ...this.host.extraCommands]);
  }

  /** Why a change to the picture may not run in this turn, or null when it may. */
  private overBudget(command: AgentCommand, budget: TurnBudget): string | null {
    if (command.type === 'dataset.load' && budget.loads >= MAX_AI_DATASET_LOADS_PER_TURN) {
      return `Not run: at most ${MAX_AI_DATASET_LOADS_PER_TURN} datasets in one answer. Say that you show one thing at a time.`;
    }
    if (budget.screenChanges >= MAX_AI_SCREEN_CHANGES_PER_TURN) {
      return `Not run: at most ${MAX_AI_SCREEN_CHANGES_PER_TURN} changes to the sphere in one answer.`;
    }
    return null;
  }

  /** Holds a change to the picture until a second has passed since the last one began. */
  private async paceScreenChange(session: Session, signal: AbortSignal): Promise<void> {
    const wait = session.lastScreenChange + AI_MIN_SCREEN_CHANGE_GAP_MS - this.now();
    if (wait > 0) await (this.host.wait ?? defaultWait)(wait, signal);
    session.lastScreenChange = this.now();
  }

  private run(context: AiTurnContext, command: AgentCommand): Promise<AgentForwarderResult> {
    return context.execute ? context.execute(command) : this.host.executeAgentCommand(command);
  }

  /** What the sphere shows, when this surface may ask (a scoped surface without state.describe does not). */
  private async observe(context: AiTurnContext): Promise<AgentObservation | null> {
    if (!this.allowed(context, 'state.describe')) return null;
    try {
      const described = await this.run(context, { type: 'state.describe' });
      return described.observation ?? (described.result as AgentObservation | undefined) ?? null;
    } catch {
      return null;
    }
  }

  /**
   * One model round, whole: its words and its tool calls. The words are held
   * until the round ends so they can be checked before they are shown (the
   * AYNI gateway sends a round in one piece anyway).
   */
  private async streamRound(
    round: { endpoint: ChatEndpoint; messages: ChatMessage[]; tools: AiToolDefinition[]; signal: AbortSignal; headers?: Record<string, string> }
  ): Promise<{ text: string; calls: ChatToolCall[] }> {
    const stream = (this.host.streamChat ?? streamChatCompletion)(round);
    let text = '';
    let calls: ChatToolCall[] = [];
    for await (const event of stream) {
      if (event.type === 'text') text += event.text;
      else if (event.type === 'tool_calls') calls = event.calls;
    }
    return { text, calls };
  }

  private async runToolCall(
    call: ChatToolCall,
    turn: {
      context: AiTurnContext;
      offered: ReadonlySet<AgentCommand['type']> | undefined;
      request: AiChatSendRequest;
      session: Session;
      budget: TurnBudget;
      signal: AbortSignal;
      emit: (event: AiChatEvent) => void;
      titles: Map<string, string>;
    }
  ): Promise<{ valid: boolean; acted: boolean; content: string }> {
    const { context, offered, request, session, budget, signal, emit, titles } = turn;
    const { sessionId, turnId } = request;
    const parsed = toolCallToAgentCommand(call.function.name, call.function.arguments, offered);
    if (!parsed.ok) {
      return { valid: false, acted: false, content: clipResult({ error: parsed.message }) };
    }
    const command = parsed.command;
    if (!READS_ONLY.has(command.type)) {
      const refusal = this.overBudget(command, budget);
      if (refusal) return { valid: true, acted: false, content: clipResult({ error: refusal }) };
      budget.screenChanges++;
      if (command.type === 'dataset.load') budget.loads++;
      await this.paceScreenChange(session, signal);
    }
    if (command.type === 'khipu.list') {
      const entries = this.host.listKhipus ? await this.host.listKhipus(command.query, command.limit ?? 8) : [];
      const outcome = { success: true as const, result: { khipus: entries, count: entries.length } };
      emit({ type: 'action', sessionId, turnId, tool: command.type, ok: true, summary: String(entries.length) });
      return { valid: true, acted: true, content: clipResult(outcome.result) };
    }
    if (command.type === 'catalog.search') {
      const results = await this.host.searchCatalog(command.query, command.limit ?? 8);
      const compact = results.map((entry) => ({ id: entry.id, title: entry.title, categories: entry.categories }));
      for (const entry of compact) titles.set(entry.id, entry.title);
      const outcome = { success: true as const, result: { results: compact, count: compact.length } };
      emit({ type: 'action', sessionId, turnId, tool: command.type, ok: true, summary: summarizeAction(command, outcome) });
      return { valid: true, acted: true, content: clipResult(outcome.result) };
    }
    const result = await this.run(context, command);
    const summary = summarizeAction(command, result, titles);
    emit({ type: 'action', sessionId, turnId, tool: command.type, ok: result.success, summary });
    const nowShowing = result.observation?.currentDataset?.title ?? (command.type === 'dataset.load' ? summary : undefined);
    return {
      valid: true,
      acted: result.success,
      content: clipResult(
        result.success ? { result: resultForMode(result.result, context.mode), nowShowing } : { error: result.error },
        AI_TOOL_RESULT_CHAR_LIMITS[command.type]
      )
    };
  }
}

